import React, { useEffect, useState, useRef } from "react";
import AuditSelect from './AuditSelect';
import { useGetUserCompanies } from "../../backend/query";
import { useHistory } from "raviger";
import { sortBy } from "underscore";
import { getUserDetails } from "../../backend/auth";
import { useAssociateCompanyLogo } from "../../contexts/AssociateCompanyContext";


const INCLUDE_USER_COMPANY_ASSOCIATIONS = {
  parent_company: {
    associate_companies: {
      company_location_mappings: {
        location: ['city']
      }
    }
  }
};

const ALL_OPTION = {
  value: "all",
  label: "All"
};

function Location({ onChange, previousFilters }: any) {
    const { state }: any = useHistory();
    const [companies, setCompanies] = useState<any[]>([]);
    const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    // No "All" option for company either — matches the Compliance Dashboard: a
    // concrete company is always selected once the list resolves (see the
    // "companies first load" effect below).
    const [company, setCompany] = useState<any>(null);
    // "All" is back for associate company, matching the Compliance Dashboard
    // revert: it sits at ALL_OPTION until the user manually picks a specific
    // one, or a specific one is restored from back-navigation. When it's "All",
    // the sidenav logo shows the parent company's own branding (company.company —
    // see the company-change effect below) rather than an auto-selected specific AC.
    const [associate_company, setAssociateCompany] = useState<any>(ALL_OPTION);
    const [location, setLocation] = useState<any>(ALL_OPTION);
    const initialLoadComplete = useRef(false);
    const user = getUserDetails();
    const { setAssociateCompanyBranding } = useAssociateCompanyLogo();

    const { userCompanies, isFetching }: any = useGetUserCompanies({
      filters: {
        users_id_eq: user.user_id,
        is_active_eq: true
      },
      includes: INCLUDE_USER_COMPANY_ASSOCIATIONS,
    });

    useEffect(() => {
        setLocations([]);
        setLocation(ALL_OPTION);

        if (company && company.value !== "all") {
            const associateCompanies = (company.company.associate_companies || []).map((associate_company: any) => {
                return {
                    label: associate_company.name,
                    value: associate_company.id,
                    associate_company: associate_company,
                    locations: associate_company.locations,
                    logo: associate_company.logo,
                    favicon: associate_company.favicon,
                    whitelabeling_enabled: associate_company.whitelabeling_enabled,
                    parent_company_id: associate_company.parent_company_id,
                };
            });
            const sorted: any[] = sortBy(associateCompanies, 'label');
            setAssociateCompanies(sorted);

            // Restore a previously-selected associate company (back-navigation) on the
            // very first load only; otherwise reset to "All" — which shows the parent
            // company's own branding (company.company, the same record already used
            // above to build the associate company list — no separate fetch needed,
            // so this applies immediately with no race/gap).
            const restored = (!initialLoadComplete.current && Object.keys(previousFilters || {}).length &&
                company.value === ((previousFilters || {}).company || {}).value)
                ? sorted.find((ac: any) => ac.value === ((previousFilters || {}).associate_company || {}).value)
                : null;
            if (restored) {
                setAssociateCompany(restored);
                setAssociateCompanyBranding(restored);
            } else {
                setAssociateCompany(ALL_OPTION);
                setAssociateCompanyBranding(company.company);
            }
        } else {
            setAssociateCompanies([]);
            setAssociateCompany(ALL_OPTION);
            // Don't clear the logo here — company is only ever null/"all"-less at this
            // point because the company list hasn't loaded/auto-selected yet (see the
            // companies-first-load effect below), not because there's genuinely
            // nothing to show. Clearing it here flashes the default ezyCOMP logo
            // before the real one resolves.
        }
    }, [company]);

    useEffect(() => {
        setLocations([]);
        setLocation(ALL_OPTION);

        if (associate_company && associate_company.value !== "all" && company && company.value !== "all") {
            const locations = (associate_company.locations || []).map((location: any) => {
                return { label: `${location.name}, ${location.city.name}`, value: location.id, location, state_id: location.state.id };
            });
            const sorted = sortBy(locations, 'label');
            setLocations(sorted);
        }
    }, [associate_company]);

    // Builds the (sorted) companies list from the fetched userCompanies AND decides
    // the default selection in the SAME pass — deliberately not split across two
    // effects (one to compute `companies`, another reacting to it), because that
    // split left a one-render gap where isFetching had already gone false but the
    // local `companies` state hadn't been populated yet, making this look like
    // "loaded, but zero companies" for a frame and flashing the default ezyCOMP logo.
    useEffect(() => {
        if (isFetching || !userCompanies) return; // still loading — leave the logo hidden, not defaulted
        const sorted: any[] = sortBy(
            userCompanies.map((c: any) => ({ value: c.id, label: c.name, company: c })),
            'label'
        );
        setCompanies(sorted);

        if (sorted.length === 0) {
            // Loaded, but this user has no companies at all — nothing will ever
            // populate the logo otherwise, so resolve to the default now.
            setAssociateCompanyBranding(null);
            return;
        }

        // No "All" option for company either — restore a previously-selected one
        // (back-navigation) or default to the first one alphabetically, same as the
        // Compliance Dashboard.
        const restored = Object.keys(previousFilters || {}).length
            ? sorted.find((c: any) => c.value === ((previousFilters || {}).company || {}).value)
            : null;
        const next = restored || sorted[0] || null;
        if (!company && next) {
            setCompany(next);
        }
    }, [userCompanies, isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

    // (Associate company restore-from-back-navigation now happens directly in the
    // company-change effect above, since it also needs to fall back to "All"
    // when there's nothing to restore.)

    useEffect(() => {
        if (initialLoadComplete.current) return;
        if (locations && locations.length > 0 && Object.keys(previousFilters || {}).length) {
            if (company && company.value === ((previousFilters || {}).company || {}).value &&
                associate_company && associate_company.value === ((previousFilters || {}).associate_company || {}).value) {
                const _location = locations.find((loc: any) => loc.value === ((previousFilters || {}).location || {}).value);
                if (_location) {
                    setLocation(_location);
                }
                initialLoadComplete.current = true;
            }
        }
        if (!Object.keys(previousFilters || {}).length && locations && locations.length > 0) {
            initialLoadComplete.current = true;
        }
    }, [locations]);

    useEffect(() => {
        // Don't call onChange until companies are loaded
        if (!companies || companies.length === 0) {
            return;
        }

        const filters: any = {};
        const filterData: any = {};

        if (company && company.value === "all") {
            const allCompanyIds = companies.map((c: any) => c.value);
            if (allCompanyIds.length > 0) {
                filters.company_id_in = allCompanyIds;
            }
        } else if (company && company.value !== "all") {
            filters.company_id_eq = company.value;
            filterData.company = company;
        }

        if (associate_company && associate_company.value !== "all") {
            filters.associate_company_id_eq = associate_company.value;
            filterData.associate_company = associate_company;
        }

        if (location && location.value !== "all") {
            filters.location_id_eq = location.value;
            filters.state_id = location.state_id;
            filterData.location = location;
        }

        onChange(filters, filterData);
    }, [company, associate_company, location, companies]);

    return (
        <>
            <div className="px-2">
                <label className="filter-label"><small>Company</small></label>
                <AuditSelect
                    placeholder='Company'
                    options={companies}
                    onChange={setCompany}
                    value={company}
                />
            </div>
            <div className="px-2">
                <label className="filter-label"><small>Associate Company</small></label>
                <AuditSelect
                    placeholder='Associate Company'
                    options={associateCompanies.length > 0 ? [ALL_OPTION, ...associateCompanies] : [ALL_OPTION]}
                    onChange={(opt: any) => {
                        const isAll = !opt || opt.value === "all";
                        setAssociateCompany(opt || ALL_OPTION);
                        if (isAll) {
                            // "All" shows the parent company's own branding — company.company
                            // is already loaded (it's what built the associate company list
                            // above), so this applies immediately, with no dependency on a
                            // separate fetch that might not have resolved yet.
                            if (company && company.company) {
                                setAssociateCompanyBranding(company.company);
                            }
                        } else {
                            setAssociateCompanyBranding(opt);
                        }
                    }}
                    value={associate_company}
                    isDisabled={!company || company.value === "all"}
                />
            </div>
            <div className="px-2">
                <label className="filter-label"><small>Location</small></label>
                <AuditSelect
                    placeholder='Location'
                    options={locations.length > 0 ? [ALL_OPTION, ...locations] : [ALL_OPTION]}
                    onChange={setLocation}
                    value={location}
                    isDisabled={!associate_company || associate_company.value === "all"}
                />
            </div>
        </>
    )
}

export default Location;
